import { CONTACT } from "@/lib/brand";
import React, { useEffect, useState } from "react";
import { Routes, Route, Link } from "react-router-dom";
import PortalLayout from "./PortalLayout";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    LayoutDashboard, Heart, CalendarCheck, MessageSquare, FileText, Settings,
    MapPin, ArrowRight, Home as HomeIcon, TrendingUp, ShieldCheck,
} from "lucide-react";

const NAV = [
    { to: "/portal/buyer",              l: "Dashboard",         icon: LayoutDashboard, end: true },
    { to: "/portal/buyer/saved",        l: "Saved Properties",  icon: Heart },
    { to: "/portal/buyer/visits",       l: "Site Visits",       icon: CalendarCheck },
    { to: "/portal/buyer/enquiries",    l: "My Enquiries",      icon: MessageSquare },
    { to: "/portal/buyer/documents",    l: "Documents",         icon: FileText },
    { to: "/portal/buyer/settings",     l: "Settings",          icon: Settings },
];

export default function BuyerPortal() {
    return (
        <Routes>
            <Route element={<PortalLayout role="buyer" brand="Buyer Portal" navItems={NAV}/>}>
                <Route index element={<Dashboard/>} />
                <Route path="saved" element={<Saved/>} />
                <Route path="visits" element={<Visits/>} />
                <Route path="enquiries" element={<Enquiries/>} />
                <Route path="documents" element={<Documents/>} />
                <Route path="settings" element={<Settings2/>} />
            </Route>
        </Routes>
    );
}

function Dashboard() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/buyer/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);
    if (!data) return <SkeletonCards />;
    const s = data.stats;
    return (
        <div className="space-y-6">
            <div className="grid md:grid-cols-3 gap-4">
                <StatCard Icon={Heart}         v={s.saved}       l="Saved Properties" trend="+2 this week" testid="stat-saved"/>
                <StatCard Icon={CalendarCheck} v={s.site_visits} l="Site Visits"       trend="Next: Skyline · Tue" testid="stat-visits"/>
                <StatCard Icon={MessageSquare} v={s.enquiries}   l="Active Enquiries"  trend="1 awaiting reply" testid="stat-enquiries"/>
            </div>
            <div className="grid lg:grid-cols-3 gap-6">
                <Panel title="Recommended for You" cta={{ to: "/properties", l: "Browse All" }}>
                    <p className="text-black/55 text-sm">Based on your saved shortlists, we suggest exploring:</p>
                    <ul className="mt-4 space-y-3">
                        {["Skyline Residences · Mumbai", "The Camellias · Gurugram", "Prestige Golfshire · Bangalore"].map((n) => (
                            <li key={n} className="flex items-center justify-between py-2 border-b border-black/8 last:border-b-0">
                                <span className="text-[14px]">{n}</span>
                                <Link to="/properties" className="text-[var(--ebg-gold)] text-[11px] uppercase tracking-[0.22em] font-bold">View →</Link>
                            </li>
                        ))}
                    </ul>
                </Panel>
                <Panel title="Legally Verified Guarantee">
                    <div className="flex items-center gap-3">
                        <span className="w-12 h-12 rounded-full bg-[var(--ebg-gold)]/10 border border-[var(--ebg-gold)]/40 text-[var(--ebg-gold)] flex items-center justify-center"><ShieldCheck size={20}/></span>
                        <div>
                            <div className="font-bold">Every property verified.</div>
                            <div className="text-black/50 text-[12px]">7-step legal check on every listing.</div>
                        </div>
                    </div>
                    <Link to="/legal-check" className="mt-4 inline-flex items-center gap-2 text-[var(--ebg-gold)] text-[11px] uppercase tracking-[0.22em] font-bold">Learn More <ArrowRight size={12}/></Link>
                </Panel>
                <Panel title="Your Advisor">
                    <div className="flex items-center gap-3">
                        <img src="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=200&q=80" alt="" className="w-12 h-12 rounded-full object-cover"/>
                        <div>
                            <div className="font-bold">Rohit Sharma</div>
                            <div className="text-black/50 text-[12px]">Senior Advisor · 10+ yrs</div>
                        </div>
                    </div>
                    <a href={CONTACT.tel} className="mt-4 block bg-[var(--ebg-gold)] text-black text-center py-3 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-black hover:text-[var(--ebg-gold)]">Book Consultation</a>
                </Panel>
            </div>
        </div>
    );
}

function Saved() {
    const [items, setItems] = useState([]);
    const load = () => api.get("/buyer/saved").then((r) => setItems(r.data.items || []));
    useEffect(() => { load(); }, []);
    const remove = async (e, id) => {
        e.preventDefault();
        e.stopPropagation();
        try { await api.delete(`/buyer/saved/${id}`); toast.success("Removed from saved"); load(); }
        catch (err) { toast.error(formatApiError(err)); }
    };
    return (
        <Panel title={`Saved Properties (${items.length})`} testid="saved-list">
            {items.length === 0 ? (
                <EmptyState msg="You haven't saved anything yet." cta={{ to: "/properties", l: "Browse properties" }}/>
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map((p) => (
                        <Link to={`/properties/${p.slug}`} key={p.id} className="group border border-black/8 bg-white overflow-hidden relative" data-testid={`saved-card-${p.slug || p.id}`}>
                            <div className="aspect-[4/3] bg-black/5 overflow-hidden">
                                <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
                            </div>
                            <button
                                onClick={(e) => remove(e, p.id)}
                                data-testid={`saved-remove-${p.slug || p.id}`}
                                className="absolute top-3 right-3 bg-white/95 text-black/60 hover:text-red-600 w-8 h-8 rounded-full border border-black/10 flex items-center justify-center"
                                title="Remove from saved"
                            >
                                <Heart size={14} fill="currentColor" className="text-red-500"/>
                            </button>
                            {p.trust_score?.total > 0 && (
                                <span className="absolute top-3 left-3 bg-[var(--ebg-gold)] text-black px-2 py-1 text-[10px] uppercase tracking-[0.18em] font-bold">
                                    Trust {p.trust_score.total}/100
                                </span>
                            )}
                            <div className="p-4">
                                <div className="font-serif text-lg">{p.title}</div>
                                <div className="text-black/50 text-[12px] flex items-center gap-1 mt-1"><MapPin size={11}/> {p.city}</div>
                                <div className="text-[var(--ebg-gold)] font-bold text-[15px] mt-2">{p.price_display}</div>
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </Panel>
    );
}

function Visits() {
    const [items, setItems] = useState([]);
    useEffect(() => { api.get("/buyer/site-visits").then((r) => setItems(r.data.items || [])); }, []);
    return (
        <Panel title="Site Visits" testid="visits-list">
            {items.length === 0 ? (
                <EmptyState msg="No site visits scheduled." cta={{ to: "/properties", l: "Book a visit" }}/>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[640px]">
                        <thead><tr className="text-left text-[10px] uppercase tracking-[0.18em] text-black/50 border-b border-black/10">
                            <th className="pb-3">Property</th><th className="pb-3">Date</th><th className="pb-3">Time</th><th className="pb-3">Status</th>
                        </tr></thead>
                        <tbody>
                            {items.map((v) => (
                                <tr key={v.id} className="border-b border-black/8 last:border-b-0" data-testid={`buyer-visit-${v.id}`}>
                                    <td className="py-3">
                                        <div className="font-bold text-[13px]">{v.property_title}</div>
                                        <div className="text-black/50 text-[11px]">{v.property_city || ""}</div>
                                    </td>
                                    <td className="py-3 text-[13px]">{v.preferred_date || "TBD"}</td>
                                    <td className="py-3 text-black/70 text-[12px]">{v.preferred_time || v.slot || "TBD"}</td>
                                    <td className="py-3"><Badge tone={v.status === "confirmed" || v.status === "completed" ? "green" : v.status === "cancelled" || v.status === "no_show" ? "red" : "gold"}>{v.status || "scheduled"}</Badge></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Panel>
    );
}

function Enquiries() {
    const [items, setItems] = useState([]);
    useEffect(() => { api.get("/buyer/enquiries").then((r) => setItems(r.data.items || [])); }, []);
    return (
        <Panel title="Your Enquiries" testid="enquiries-list">
            {items.length === 0 ? <EmptyState msg="No enquiries yet." cta={{ to: "/properties", l: "Explore" }}/> : (
                <ul className="divide-y divide-black/8">
                    {items.map((l) => (
                        <li key={l.id} className="py-4">
                            <div className="flex items-center justify-between">
                                <div className="font-bold text-[14px]">{l.property_title || l.message?.slice(0, 60) + "…"}</div>
                                <Badge tone="gold">{l.status || "new"}</Badge>
                            </div>
                            <div className="text-black/55 text-[12px] mt-1">{new Date(l.created_at).toLocaleString("en-IN")}</div>
                        </li>
                    ))}
                </ul>
            )}
        </Panel>
    );
}

function Documents() { return <Panel title="Documents"><EmptyState msg="Document vault opens once you complete your first booking." /></Panel>; }
function Settings2() { return <Panel title="Settings"><p className="text-black/55 text-sm">Profile settings coming soon.</p></Panel>; }

// ---------- Shared ----------
export function StatCard({ Icon, v, l, trend, testid }) {
    return (
        <div className="bg-white border border-black/8 p-6" data-testid={testid}>
            <div className="flex items-center justify-between">
                <span className="w-11 h-11 rounded-full bg-[var(--ebg-gold)]/10 border border-[var(--ebg-gold)]/40 text-[var(--ebg-gold)] flex items-center justify-center">
                    <Icon size={17}/>
                </span>
                {trend && <span className="text-[10px] text-green-600 font-bold">{trend}</span>}
            </div>
            <div className="font-serif text-4xl mt-4 tabular-nums">{v}</div>
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-black/55 mt-2">{l}</div>
        </div>
    );
}

export function Panel({ title, children, cta, testid }) {
    return (
        <section className="bg-white border border-black/8 p-6" data-testid={testid}>
            <div className="flex items-center justify-between mb-4">
                <h3 className="font-serif text-xl">{title}</h3>
                {cta && <Link to={cta.to} className="text-[var(--ebg-gold)] text-[11px] uppercase tracking-[0.22em] font-bold">{cta.l} →</Link>}
            </div>
            {children}
        </section>
    );
}

export function Badge({ children, tone = "gold" }) {
    const cls = tone === "green" ? "bg-green-100 text-green-700 border-green-500/25" :
        tone === "red" ? "bg-red-100 text-red-700 border-red-500/25" :
        "bg-[var(--ebg-gold)]/10 text-[var(--ebg-gold)] border-[var(--ebg-gold)]/40";
    return <span className={`inline-block px-2.5 py-1 border text-[10px] uppercase tracking-[0.18em] font-bold ${cls}`}>{children}</span>;
}

export function EmptyState({ msg, cta }) {
    return (
        <div className="py-16 text-center border border-dashed border-black/12">
            <p className="text-black/50">{msg}</p>
            {cta && <Link to={cta.to} className="mt-4 inline-flex items-center gap-2 text-[var(--ebg-gold)] text-[11px] uppercase tracking-[0.22em] font-bold">{cta.l} <ArrowRight size={12}/></Link>}
        </div>
    );
}

export function SkeletonCards() {
    return (
        <div className="grid md:grid-cols-3 gap-4">
            {[0, 1, 2].map((i) => <div key={i} className="h-40 bg-white border border-black/8 animate-pulse"/>)}
        </div>
    );
}
